import { useEventListener } from '@vueuse/core';
import { getCookieValue, setCookie } from '@/js/helpers/cookieHelpers';
import { sendPageViewData } from './api';
import { SiteIdEnum } from '@/js/enums/siteIds';
import { PageViewPayload, TrackingData } from '@/js/pages/helpers/tracking-script/types';
import { IdentifierService } from '@/js/pages/helpers/tracking-script/identifierService';

const getGoogleAnalyticsClientId = (cookieValue: string | null): string | null => {
    if (!cookieValue) {
        return null;
    }
    const parts = cookieValue.split('.');

    return parts.length > 2 ? `${parts[parts.length - 2]}.${parts[parts.length - 1]}` : null;
};

const getQueryParameters = (): Record<string, string> => {
    const params = new URLSearchParams(window.location.search);

    return Object.fromEntries(params.entries());
};

const waitForGaCookie = (timeout = 10000): Promise<string | null> => {
    const pollInterval = 500;
    const endTime = Date.now() + timeout;

    return new Promise((resolve) => {
        const checkCookie = () => {
            const gaCookie = getCookieValue('_ga');
            if (gaCookie) {
                resolve(gaCookie);
            } else if (Date.now() >= endTime) {
                resolve(null);
            } else {
                setTimeout(checkCookie, pollInterval);
            }
        };
        checkCookie();
    });
};

const getTrackingData = (cookieName: string) => {
    const cookieValue = getCookieValue(cookieName);
    if (!cookieValue) {
        return {};
    }

    try {
        return JSON.parse(cookieValue);
    } catch (e) {
        return {};
    }
};

const updateVulprCookie = (additionalData = null): TrackingData => {
    const cookieName = 'vulpr';
    const cookieExpiration = 730;

    const trackingData = getTrackingData(cookieName);
    if (!trackingData.client_id) {
        trackingData.client_id = crypto.randomUUID();
    }

    trackingData.latest_visited_page = window.location.href;

    if (additionalData) {
        Object.assign(trackingData, additionalData);
    }

    setCookie(cookieName, JSON.stringify(trackingData), cookieExpiration);

    return trackingData;
};

const identifierService = new IdentifierService();

const handleGoonLinkClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const link = target.closest('a[href*="/goon/"]') as HTMLAnchorElement;

    if (!link) {
        return;
    }

    const category = link.dataset.category;
    const position = link.dataset.position;

    let tag = category;
    if (category && position) {
        tag += `-${position}`;
    }

    updateVulprCookie({
        tags: [tag || identifierService.generateIdentifier(link)],
    });
};

const trackPageView = async (vulprData: Partial<TrackingData>): Promise<void> => {
    const gaCookie = await waitForGaCookie(10000);
    const gaCid = getGoogleAnalyticsClientId(gaCookie);

    let externalReferrer: string | null = null;
    if (document.referrer && !document.referrer.startsWith(window.location.origin)) {
        externalReferrer = document.referrer;
    }

    const queryParams = getQueryParameters();

    const payload: PageViewPayload = {
        external_id: crypto.randomUUID(),
        project_id: SiteIdEnum.SITE_ID,
        tracking_cookie_id: vulprData.client_id || null,
        ga_cid: gaCid,
        url: window.location.href,
        referrer_url: externalReferrer,
        user_agent: navigator.userAgent,
    };

    if (Object.keys(queryParams).length > 0) {
        payload.additional_data = {
            query_parameters: queryParams,
        };
    }

    try {
        await sendPageViewData(payload);
    } catch (error) {
        /* empty */
    }
};

useEventListener(document, 'DOMContentLoaded', () => {
    const vulprData = updateVulprCookie();

    trackPageView(vulprData);
});

useEventListener(document, 'click', handleGoonLinkClick);
